import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  Package,
  Users,
  TrendingUp,
  Plus,
  ChevronRight,
  Star,
  ArrowUpRight,
  Layers,
  ClipboardList,
  CheckCircle,
  AlertCircle,
  Sparkles,
  ShoppingBag,
} from "lucide-react";
import { device } from "../../../atoms/Utlis/media";
import {
  getMarketplaceProfile,
  getMyListings,
  getStudioBookings,
  MarketplaceProfile,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { MARKETPLACE_GRADIENT } from "./marketplaceColors";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MarketplaceDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<MarketplaceProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [stats, setStats] = useState({
    listings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchStats();
  }, []);

  const fetchProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await getMarketplaceProfile();
      setProfile(res?.data ?? null);
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const [listingsRes, bookingsRes] = await Promise.all([
        getMyListings({ page_size: 1 }),
        getStudioBookings(),
      ]);
      const bookings = bookingsRes?.data ?? [];
      setStats({
        listings: listingsRes?.count ?? 0,
        pendingBookings: bookings.filter((b: any) => b.status === "requested")
          .length,
        confirmedBookings: bookings.filter((b: any) =>
          ["confirmed", "accepted"].includes(b.status)
        ).length,
      });
    } catch {
      // stats remain at defaults
    } finally {
      setStatsLoading(false);
    }
  };

  const hasProfile = !profileLoading && !!profile;

  const quickCards = [
    {
      label: "My Listings",
      desc: "Manage your equipment for sale/rent and service listings.",
      accent: "#7c4dff",
      icon: <Layers size={24} color="#7c4dff" />,
      bg: "rgba(124,77,255,0.1)",
      onClick: () => navigate("/dashboard/marketplace/listings"),
    },
    {
      label: "Bookings",
      desc: "Accept, reject and manage incoming booking requests.",
      accent: "#f09a3e",
      icon: <ClipboardList size={24} color="#f09a3e" />,
      bg: "rgba(240,154,62,0.1)",
      onClick: () => navigate("/dashboard/marketplace/bookings"),
    },
    {
      label: "Requirements",
      desc: "Post or browse equipment and service needs from the community.",
      accent: "#16a34a",
      icon: <Package size={24} color="#16a34a" />,
      bg: "rgba(22,163,74,0.1)",
      onClick: () => navigate("/dashboard/marketplace/requirements"),
    },
    {
      label: "Applications",
      desc: "View applications you submitted and manage received ones.",
      accent: "#7c4dff",
      icon: <Users size={24} color="#7c4dff" />,
      bg: "rgba(124,77,255,0.1)",
      onClick: () => navigate("/dashboard/marketplace/applications"),
    },
    {
      label: "Activity Summary",
      desc: "View your rentals, inquiries, purchases and recruitment at a glance.",
      accent: "#0ea5e9",
      icon: <TrendingUp size={24} color="#0ea5e9" />,
      bg: "rgba(14,165,233,0.1)",
      onClick: () => navigate("/dashboard/marketplace/activity"),
    },
    {
      label: "Home Dashboard",
      desc: "Access the full interactive marketplace home dashboard overview.",
      accent: "#c94daa",
      icon: <Camera size={24} color="#c94daa" />,
      bg: "rgba(201,77,170,0.1)",
      onClick: () => navigate("/marketplace/dashboard"),
    },
  ];

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Creator marketplace
              </HeroBadge>
              <PageTitle className="linear-text">Marketplace</PageTitle>
              <PageSubtitle>
                Connect with studios, freelancers and buyers. Sell, rent or hire — all in one place.
              </PageSubtitle>
            </div>

          </HeroText>
          <HeaderActions>
            <SecondaryBtn onClick={() => window.open("/marketplace", "_blank")}>
              <ArrowUpRight size={16} /> Go to marketplace
            </SecondaryBtn>
            {hasProfile && (
              <PrimaryBtn
                onClick={() => navigate("/dashboard/marketplace/listings/new")}
              >
                <Plus size={16} /> New listing
              </PrimaryBtn>
            )}
          </HeaderActions>
        </HeroContent>
      </Hero>

      {!profileLoading && !profile && (
        <ProfileBanner>
          <BannerIconWrap>
            <AlertCircle size={28} color="#c94daa" />
          </BannerIconWrap>
          <BannerContent>
            <BannerTitle>Set up your marketplace profile</BannerTitle>
            <BannerDesc>
              Create a profile to start listing your equipment and services publicly.
            </BannerDesc>
          </BannerContent>
          <PrimaryBtn onClick={() => navigate("/dashboard/marketplace/profile")}>
            Create profile <ChevronRight size={16} />
          </PrimaryBtn>
        </ProfileBanner>
      )}

      {hasProfile && (
        <ProfileCard>
          <ProfileAvatarCircle>
            {profile!.display_name.charAt(0).toUpperCase()}
          </ProfileAvatarCircle>
          <ProfileInfo>
            <ProfileName>{profile!.display_name}</ProfileName>
            <ProfileMeta>
              {profile!.city && <span>{profile!.city}</span>}
              {profile!.is_seller && <Tag color="#7c4dff">Seller</Tag>}
              {profile!.is_professional && <Tag color="#c94daa">Professional</Tag>}
              {profile!.is_verified && (
                <Tag color="#16a34a">
                  <CheckCircle size={11} /> Verified
                </Tag>
              )}
            </ProfileMeta>
          </ProfileInfo>
          <EditProfileBtn onClick={() => navigate("/dashboard/marketplace/profile")}>
            Edit profile
          </EditProfileBtn>
        </ProfileCard>
      )}

      <StatsGrid>
        <WalletHero>
          <WalletIconWrap>
            <ShoppingBag size={16} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>{statsLoading ? "—" : stats.listings}</WalletHeroValue>
            <WalletHeroLabel>Active listings</WalletHeroLabel>
            <WalletHeroFooter onClick={() => navigate("/dashboard/marketplace/listings")}>
              Manage listings →
            </WalletHeroFooter>
          </WalletHeroBody>
        </WalletHero>

        <StatCard $color="#f09a3e" style={{ animationDelay: "60ms" }}>
          <StatIcon $bg="rgba(240,154,62,0.12)">
            <ClipboardList size={16} color="#f09a3e" />
          </StatIcon>
          <StatBody>
            <StatValue>{statsLoading ? "—" : stats.pendingBookings}</StatValue>
            <StatLabel>Pending bookings</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $color="#16a34a" style={{ animationDelay: "120ms" }}>
          <StatIcon $bg="rgba(22,163,74,0.12)">
            <CheckCircle size={16} color="#16a34a" />
          </StatIcon>
          <StatBody>
            <StatValue>{statsLoading ? "—" : stats.confirmedBookings}</StatValue>
            <StatLabel>Confirmed bookings</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $color="#c94daa" style={{ animationDelay: "180ms" }}>
          <StatIcon $bg="rgba(201,77,170,0.12)">
            <Star size={16} color="#c94daa" />
          </StatIcon>
          <StatBody>
            <StatValue>{hasProfile ? "Live" : "—"}</StatValue>
            <StatLabel>Public profile</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      <SectionHeader>
        <SectionTitle>Quick access</SectionTitle>
        <SectionHint>Jump into the tools you use most</SectionHint>
      </SectionHeader>

      <QuickGrid>
        {quickCards.map((card) => (
          <QuickCard
            key={card.label}
            onClick={card.onClick}
            $accent={card.accent}
            style={{ animationDelay: "0ms" }}
          >
            <QuickIcon $bg={card.bg}>{card.icon}</QuickIcon>
            <QuickLabel>{card.label}</QuickLabel>
            <QuickDesc>{card.desc}</QuickDesc>
            <QuickArrow>
              <ChevronRight size={18} />
            </QuickArrow>
          </QuickCard>
        ))}
      </QuickGrid>
    </PageContainer>
  );
};

export default MarketplaceDashboardPage;

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 28px 26px;
  background: linear-gradient(
    135deg,
    rgba(124, 77, 255, 0.14) 0%,
    rgba(201, 77, 170, 0.12) 50%,
    rgba(255, 201, 81, 0.1) 100%
  );
  border: 1px solid rgba(124, 77, 255, 0.12);
  box-shadow: 0 8px 28px rgba(124, 77, 255, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0px;
  right: -5%;
  width: 249px;
  height: 150px;
  // border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(124, 77, 255, 0.28) 0%,
    transparent 70%
  );
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(124, 77, 255, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #7c4dff;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${MARKETPLACE_GRADIENT};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.24);
  white-space: nowrap;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: rgba(23, 23, 23, 0.75);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  &:hover {
    background: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const ProfileBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 18px;
  background: linear-gradient(135deg, #fdf4ff 0%, #f5f0ff 100%);
  border: 1px solid rgba(201, 77, 170, 0.18);
  flex-wrap: wrap;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(201, 77, 170, 0.08);
`;

const BannerIconWrap = styled.div`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 77, 170, 0.1);
`;

const BannerContent = styled.div`
  flex: 1;
  min-width: 200px;
`;

const BannerTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const BannerDesc = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.6);
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 28px rgba(23, 23, 23, 0.06);
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const ProfileAvatarCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${MARKETPLACE_GRADIENT};
  color: white;
  font-size: 22px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.22);
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileName = styled.h3`
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
`;

const ProfileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  font-family: Montserrat, sans-serif;
  color: rgba(23, 23, 23, 0.6);
`;

const Tag = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => p.color}18;
  color: ${(p) => p.color};
`;

const EditProfileBtn = styled.button`
  padding: 10px 16px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  background: #fff;
  color: rgba(23, 23, 23, 0.75);
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  &:hover {
    background: #f8fafc;
    border-color: rgba(124, 77, 255, 0.2);
    color: #7c4dff;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(144.01deg, #7c4dff 0%, #c94daa 100%);
  box-shadow: 0 8px 22px rgba(124, 77, 255, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.2);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const WalletHeroFooter = styled.button`
  position: relative;
  z-index: 1;
  margin-top: 4px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.88);
  font-family: Urbanist, sans-serif;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  padding: 0;
  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

const StatCard = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: white;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
  animation: ${fadeUp} 0.4s ease both;
`;

const StatIcon = styled.div<{ $bg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: ${(p) => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  margin: 0;
  color: #171717;
`;

const SectionHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.52);
`;

const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  flex-shrink: 0;
  @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const QuickIcon = styled.div<{ $bg: string }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${(p) => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const QuickLabel = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #171717;
  margin-bottom: 6px;
`;

const QuickDesc = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.55);
  line-height: 1.45;
  padding-right: 24px;
`;

const QuickArrow = styled.div`
  position: absolute;
  top: 20px;
  right: 16px;
  color: rgba(23, 23, 23, 0.28);
  transition: all 0.2s ease;
`;

const QuickCard = styled.div<{ $accent: string }>`
  position: relative;
  padding: 20px 18px;
  border-radius: 16px;
  background: white;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 22px rgba(23, 23, 23, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(23, 23, 23, 0.1);
    border-color: ${(p) => p.$accent}44;
  }

  &:hover ${QuickArrow} {
    color: ${(p) => p.$accent};
    transform: translateX(2px);
  }
`;
